import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../../utils/common";
import useUserInfo from "../../../hooks/useUserInfo";
import dayjs from "dayjs";

const KYC_DOCUMENTS = [
  "GST Certificate",
  "Business PAN",
  "Debt profile",
  "Bank statements for all bank accounts for the last 12 months",
  "GSTR-3B returns for the last 12 months",
  "Cancelled cheque",
  "Security Cheque",
];

const UploadKycDocumentModal = ({
  open,
  onClose,
  borrowerInfo,
  setBorrowerInfo,
}: any) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const privateInstance = useAxiosInstance();
  const user = useUserInfo();

  const [selectedDoc, setSelectedDoc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  const existingKyc = borrowerInfo?.kyc_docs || {};
  const existingDocs = existingKyc?.docs_url || [];

  const uploadedDocNames = existingDocs.map((d: any) => d.name);

  const availableDocs = KYC_DOCUMENTS.filter(
    (doc) => !uploadedDocNames.includes(doc),
  );

  /** Reset modal state when closed */
  useEffect(() => {
    if (!open) {
      setSelectedDoc("");
      setFileUrl(null);
      setProgress(0);
      setUploading(false);
    }
  }, [open]);

  const handleUpload = async (file: File) => {
    if (!selectedDoc) {
      errorToast("Please select document type");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      errorToast("Max allowed file size is 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setProgress(0);

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V1}/orders/reports/upload?created_by=${user?.user_id}&report_type=borrower`,
        formData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              setProgress(Math.round((e.loaded * 100) / e.total));
            }
          },
        },
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setFileUrl(res.data.response?.url);
        successToast("File uploaded successfully");
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!fileUrl || !selectedDoc) return;

    const updatedDocs = [...existingDocs, { name: selectedDoc, url: fileUrl }];

    const status =
      updatedDocs.length === KYC_DOCUMENTS.length ? "completed" : "pending";

    const payload = {
      borrower_id: borrowerInfo.borrower_id,
      kyc_docs: {
        status,
        date_of_submission: dayjs().format("DD MMM, YYYY HH:mm"),
        docs_url: updatedDocs,
      },
    };

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setBorrowerInfo((prev: any) => ({
          ...prev,
          kyc_docs: payload.kyc_docs,
        }));

        successToast("KYC document uploaded successfully");
        onClose(); // ✅ parent handles modal state
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleRemove = () => {
    setFileUrl(null);
    setProgress(0);
  };

  const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <CustomDialogTitle id="customized-dialog-title" onClose={onClose}>
        Upload KYC Document
      </CustomDialogTitle>

      <DialogContent>
        <TextField
          select
          fullWidth
          size="small"
          label="Select Document"
          value={selectedDoc}
          onChange={(e) => setSelectedDoc(e.target.value)}
          sx={{ mb: 2, mt: 2 }}
          disabled={!availableDocs.length}
        >
          <MenuItem value="">Select</MenuItem>
          {availableDocs.map((doc) => (
            <MenuItem key={doc} value={doc}>
              {doc}
            </MenuItem>
          ))}
        </TextField>

        {!fileUrl && (
          <Box
            onClick={() => !uploading && inputRef.current?.click()}
            sx={{
              border: "2px dashed #3f6ee8",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: selectedDoc ? "pointer" : "not-allowed",
              backgroundColor: "#fafbff",
              opacity: selectedDoc ? 1 : 0.6,
            }}
          >
            {uploading ? (
              <>
                <CircularProgress variant="determinate" value={progress} />
                <Typography mt={1}>Uploading… {progress}%</Typography>
              </>
            ) : (
              <>
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 48, color: "#3f6ee8" }}
                />
                <Typography mt={1} fontWeight={500}>
                  Click to upload
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF / Image • Max 10 MB
                </Typography>
              </>
            )}

            <input
              hidden
              ref={inputRef}
              type="file"
              accept="image/*,application/pdf"
              disabled={!selectedDoc}
              onChange={(e) =>
                e.target.files && handleUpload(e.target.files[0])
              }
            />
          </Box>
        )}

        {fileUrl && (
          <Box mt={2} p={2} border="1px solid #ddd" borderRadius={2}>
            {isPdf ? (
              <Box display="flex" alignItems="center" gap={1}>
                <PictureAsPdfIcon color="error" />
                <Typography flex={1} fontWeight={500}>
                  {selectedDoc}
                </Typography>
                <LoadingButton
                  size="small"
                  variant="outlined"
                  onClick={() => window.open(fileUrl, "_blank")}
                >
                  View
                </LoadingButton>
                <IconButton onClick={handleRemove}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <>
                <Typography fontWeight={500}>{selectedDoc}</Typography>
                <img
                  src={fileUrl}
                  alt="preview"
                  style={{
                    width: "100%",
                    maxHeight: 300,
                    objectFit: "contain",
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                />
                <Box mt={1} textAlign="center">
                  <LoadingButton
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={handleRemove}
                  >
                    Remove
                  </LoadingButton>
                </Box>
              </>
            )}
          </Box>
        )}

        <Box textAlign="center" mt={3}>
          <LoadingButton
            variant="contained"
            disabled={!fileUrl || !selectedDoc}
            onClick={handleUpdate}
          >
            Update
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadKycDocumentModal;
